import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { palette } from "@/styles/palette";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAromaTags, fetchAlcoholDetail, createReview } from "@/api/reviewApi";
import { AromaCategory, ReviewData } from "@/types";
import ReviewForm from "@/components/review/ReviewForm";
import ConfirmPopup from "@/components/common/ConfirmPopup";
import logger from "@/utils/logger";

interface ProductDetail {
    altKey: string;
    name: string;
    type: string;
    abv: number;
    capacity: string;
    breweryInfo: {
        breweryAltKey: string;
        breweryName: string;
        productionRegion: string;
    };
    thumbnailImageUrl?: string;
}

const ReviewWritePage: React.FC = () => {
    const { altKey } = useParams<{ altKey: string }>();
    const [productData, setProductData] = useState<ProductDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [aromaTags, setAromaTags] = useState<AromaCategory[]>([]);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const navigate = useNavigate();

    // 상품 정보 조회
    useEffect(() => {
        const fetchProductData = async () => {
            if (!altKey) {
                setError("상품 정보를 찾을 수 없습니다.");
                setLoading(false);
                return;
            }

            try {
                const response = await fetchAlcoholDetail(altKey);
                setProductData(response.data);
                setLoading(false);
            } catch (error) {
                logger.error("Failed to fetch product data:", error);
                setError("상품 정보를 불러오는데 실패했습니다.");
                setLoading(false);
            }
        };

        fetchProductData();
    }, [altKey]);

    // 아로마 태그 불러오기
    useEffect(() => {
        const fetchTags = async () => {
            const cachedTags = localStorage.getItem("aromaTags");
            if (cachedTags) {
                setAromaTags(JSON.parse(cachedTags));
            } else {
                try {
                    const data = await fetchAromaTags();
                    setAromaTags(data);
                    localStorage.setItem("aromaTags", JSON.stringify(data));
                } catch (error) {
                    logger.error("Failed to fetch aroma tags:", error);
                }
            }
        };

        fetchTags();
    }, []);

    const handleSubmit = async (reviewData: ReviewData) => {
        try {
            const response = await createReview(reviewData);
            navigate("/soollog"); // 리뷰 생성 성공 시 술로그로 리다이렉트
        } catch (error: any) {
            logger.error("Error creating review:", error);

            // API 오류 응답 형식 처리
            if (error.response && error.response.status === 400) {
                const errorData = error.response.data;

                // 오류 메시지 구성
                if (errorData.message) {
                    setErrorMessage(errorData.message);
                } else if (errorData.data && Array.isArray(errorData.data)) {
                    // 모든 ValidationError 메시지를 결합하여 표시
                    const errorMessages = errorData.data.map((err: any) => err.message).join("\n");
                    setErrorMessage(errorMessages || "입력한 데이터가 유효하지 않습니다.");
                } else {
                    setErrorMessage("리뷰 작성 중 오류가 발생했습니다.");
                }

                // 팝업 표시
                setShowErrorPopup(true);
            } else {
                // 일반 오류
                setErrorMessage("리뷰 생성에 실패했습니다. 다시 시도해 주세요.");
                setShowErrorPopup(true);
            }
        }
    };

    const handleCloseErrorPopup = () => {
        setShowErrorPopup(false);
    };

    const handleBackButtonClick = () => {
        navigate(-1); // 이전 페이지로 이동
    };

    if (loading) {
        return <LoadingContainer>로딩 중...</LoadingContainer>;
    }

    // 상품 데이터가 없을 경우에도 기본 UI를 표시하고 제품명을 "제품"으로 설정
    const productName = productData?.name || "제품";

    return (
        <Container>
            <Header>
                <BackButton onClick={handleBackButtonClick}>
                    <img src="/icon/chevron-left.png" alt="뒤로가기" />
                </BackButton>
                <Title>리뷰 작성</Title>
            </Header>

            {productData && (
                <ReviewForm
                    productAltKey={productData.altKey}
                    productName={productData.name}
                    aromaTags={aromaTags}
                    onSubmit={handleSubmit}
                />
            )}

            {/* 에러 팝업 */}
            <ConfirmPopup
                isOpen={showErrorPopup}
                onClose={handleCloseErrorPopup}
                onConfirm={handleCloseErrorPopup}
                title={errorMessage}
                hideCancel={true}
            />
        </Container>
    );
};

export default ReviewWritePage;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-size: 18px;
    color: ${palette.primary};
`;

const Container = styled.div`
    width: 100%;
    min-height: 100vh;
    background-color: ${palette.white};
    position: relative;
    overflow-x: hidden; // 혹시 모를 가로 스크롤 방지
`;

const Header = styled.header`
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    background-color: ${palette.white};
    z-index: 100;
`;

const BackButton = styled.button`
    position: absolute;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;

    img {
        width: 24px;
        height: 24px;
    }
`;

const Title = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: ${palette.black};
`;
